import { Check, Filter, LayoutGrid, Lock, Play, RectangleHorizontal, RectangleVertical, Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { cn } from '@pixlide/ui';
import { useTemplates } from '@/api/queries/dashboard';
import { useCreateProjectFromTemplate } from '@/api/queries/projects';
import { frameClassForFormat } from '@/lib/video-format';
import type { components } from '@/api/generated/schema';

type ScreenFormat = 'vertical' | 'horizontal';
type TemplateSummary = components['schemas']['PixlideApplicationFeaturesTemplatesTemplateListItem'];

/** Portrait 9:16 pour un totem/vitrine, paysage 16:9 pour un écran de salle classique. */
const formatToVideoFormat: Record<ScreenFormat, components['schemas']['PixlideDomainContentEnumsVideoFormat']> = {
  vertical: 1,
  horizontal: 0,
};

function StepBadge({ step }: { step: number }) {
  return (
    <span
      aria-hidden
      className="grid size-5 shrink-0 place-items-center rounded-full bg-ink-900 text-[11px] font-semibold text-white"
    >
      {step}
    </span>
  );
}

function SelectionCheck() {
  return (
    <span
      aria-hidden
      className="absolute top-3 right-3 grid size-5 place-items-center rounded-full bg-ink-900 text-white"
    >
      <Check className="size-3" strokeWidth={3} />
    </span>
  );
}

function FormatOption({
  format,
  selected,
  icon,
  label,
  onSelect,
}: {
  format: ScreenFormat;
  selected: boolean;
  icon: ReactNode;
  label: string;
  onSelect: (format: ScreenFormat) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(format)}
      aria-pressed={selected}
      className={cn(
        'relative flex w-40 flex-col items-center gap-3 rounded-card bg-white/90 p-6 shadow-card ring-1 ring-transparent transition',
        selected ? 'ring-2 ring-ink-900' : 'hover:ring-ink-100',
      )}
    >
      {selected ? <SelectionCheck /> : null}
      <span aria-hidden className="text-ink-500">
        {icon}
      </span>
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );
}

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: TemplateSummary;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(template.id)}
      aria-pressed={selected}
      className={cn(
        'relative flex flex-col gap-2 rounded-card bg-white/90 p-2.5 text-left shadow-card ring-1 ring-transparent transition',
        selected ? 'ring-2 ring-ink-900' : 'hover:ring-ink-100',
      )}
    >
      {selected ? <SelectionCheck /> : null}
      {template.isPremium ? (
        <span
          aria-hidden
          className="absolute top-3 right-3 grid size-5 place-items-center rounded-full bg-white text-ink-500 shadow-card"
        >
          <Lock className="size-3" />
        </span>
      ) : null}

      <div
        className={cn(
          'flex items-center justify-center overflow-hidden rounded-lg bg-ink-900',
          frameClassForFormat(template.format),
        )}
      >
        {/* Vignette décorative : le nom du template, juste en dessous, porte l'information. */}
        {template.thumbnailUrl ? (
          <img src={template.thumbnailUrl} alt="" loading="lazy" className="size-full object-cover" />
        ) : (
          <span aria-hidden className="grid size-9 place-items-center rounded-full bg-white/15 text-white">
            <Play className="size-4" fill="currentColor" />
          </span>
        )}
      </div>

      <p className="px-1 pb-1 text-sm font-medium">{template.name}</p>
    </button>
  );
}

/**
 * Sélection du format d'écran puis d'un template.
 *
 * Le format filtre réellement la liste via `GET /templates?format=`. La durée est celle du
 * template, et le nom se saisit sur la page suivante (l'éditeur) : le projet est créé ici
 * sous le nom du template, que le client remplace ensuite.
 */
export function TemplateSelectionPage() {
  const navigate = useNavigate();
  const [format, setFormat] = useState<ScreenFormat>('vertical');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [createError, setCreateError] = useState<string | undefined>(undefined);

  const templates = useTemplates({ format: formatToVideoFormat[format] });
  const createProject = useCreateProjectFromTemplate();

  const templateItems = templates.data?.items ?? [];
  const selectedTemplate = templateItems.find((item) => item.id === selectedTemplateId);

  const onContinue = async () => {
    if (!selectedTemplate) {
      return;
    }

    setCreateError(undefined);

    try {
      const projectId = await createProject.mutateAsync({
        templateId: selectedTemplate.id,
        name: selectedTemplate.name,
        // null : le rendu garde la durée du template.
        durationSeconds: null,
      });

      await navigate({ to: '/creer/template/$projectId', params: { projectId } });
    } catch {
      setCreateError(
        "Impossible de créer le projet depuis ce template — vérifiez que votre formule l'inclut.",
      );
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Création à partir d'un template</h1>
        <p className="mt-1.5 text-ink-500">
          Pour commencer la création de votre vidéo, choisissez le format de votre écran, puis
          sélectionnez un template dans la liste proposée.
        </p>
      </div>

      <section className="flex flex-col gap-4">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <StepBadge step={1} />
          Choisir le format de l'écran
        </h2>

        <div className="flex gap-4">
          <FormatOption
            format="vertical"
            selected={format === 'vertical'}
            icon={<RectangleVertical className="size-8" strokeWidth={1.25} />}
            label="Format Vertical"
            onSelect={setFormat}
          />
          <FormatOption
            format="horizontal"
            selected={format === 'horizontal'}
            icon={<RectangleHorizontal className="size-8" strokeWidth={1.25} />}
            label="Format Horizontal"
            onSelect={setFormat}
          />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <StepBadge step={2} />
            Sélectionnez votre template
            <span className="text-sm font-normal text-ink-500">
              {format === 'vertical' ? 'Portrait 9:16' : 'Paysage 16:9'}
            </span>
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="flex items-center gap-1.5 rounded-pill bg-white/90 px-3.5 py-2 text-sm font-medium text-ink-700 shadow-card ring-1 ring-transparent hover:ring-ink-100"
          >
            <Filter className="size-4" />
            Filtres
          </button>

          <button
            type="button"
            className="flex items-center gap-1.5 rounded-pill bg-white/90 px-3.5 py-2 text-sm font-medium text-ink-700 shadow-card ring-1 ring-transparent hover:ring-ink-100"
          >
            <LayoutGrid className="size-4" />
            Trier par catégories
          </button>

          <div className="relative ml-auto">
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
            />
            <input
              type="search"
              aria-label="Rechercher un template"
              placeholder="Rechercher"
              className="h-10 w-56 rounded-pill bg-white/80 pr-4 pl-9 text-sm ring-1 ring-ink-100 placeholder:text-ink-300 focus:ring-brand-500"
            />
          </div>
        </div>

        {templates.isPending ? <p className="text-sm text-ink-500">Chargement des templates…</p> : null}

        {templates.isError ? (
          <p className="text-sm text-danger-500">Impossible de charger les templates.</p>
        ) : null}

        {!templates.isPending && templateItems.length === 0 ? (
          <p className="text-sm text-ink-500">Aucun template publié pour ce format pour le moment.</p>
        ) : null}

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {templateItems.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              selected={selectedTemplateId === template.id}
              onSelect={setSelectedTemplateId}
            />
          ))}
        </div>
      </section>

      {/* Désactivé pour le moment : aucun moteur de rendu n'applique encore l'habillage. */}
      <section aria-disabled className="flex flex-col gap-4 opacity-50">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <StepBadge step={3} />
          Ajouter un habillage (optionnel)
          <span className="rounded-pill bg-ink-100 px-2.5 py-0.5 text-xs font-medium text-ink-500">
            Bientôt disponible
          </span>
        </h2>

        <div className="flex flex-wrap gap-4">
          <button
            type="button"
            disabled
            className="relative flex w-32 cursor-not-allowed flex-col items-center justify-center gap-2 rounded-card bg-white/90 p-4 text-sm font-medium text-ink-500 shadow-card"
          >
            Aucun
          </button>
        </div>
      </section>

      <div className="flex flex-col items-end gap-2">
        {createError ? <p className="text-sm text-danger-500">{createError}</p> : null}

        <button
          type="button"
          disabled={!selectedTemplate || createProject.isPending}
          onClick={() => void onContinue()}
          className="rounded-pill bg-ink-900 px-6 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {createProject.isPending ? 'Création…' : 'Continuer'}
        </button>
      </div>
    </div>
  );
}
